import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {fetchEventSource} from '@microsoft/fetch-event-source';
import {toast} from 'react-toastify';
import axios from 'axios';

import {
  getAssistantSession,
  getProgramCareTeam,
  getProgramDetail,
  getProgramLibrary,
  getProgramModules,
  getProgramNext,
  interventionAsyncActions
} from '../../redux/pageAsyncActions';

class NonRetryableError extends Error {}

function buildHeaders(): Record<string, string> {
  const common = axios.defaults.headers.common as Record<string, string>;
  const headers: Record<string, string> = {
    Authorization: common.Authorization ?? '',
    API_KEY: common.API_KEY ?? (process.env.REACT_APP_API_KEY ?? '')
  };

  if (common.CLEVER_TOKEN) {
    headers.CLEVER_TOKEN = common.CLEVER_TOKEN;
  }

  return headers;
}

function buildSseUrl(apId: number): string {
  return `${process.env.REACT_APP_API_URL ?? ''}/patient/stream/run/${apId}`;
}

export const useProgramDetailSse = (apId: number): void => {
  const dispatch = useDispatch();
  const sseAvailableRef = useRef(true);

  const reload = (events: string[]) => {
    if (events.includes('detail')) {
      dispatch(interventionAsyncActions[getProgramDetail](apId));
    }
    if (events.includes('next')) {
      dispatch(interventionAsyncActions[getProgramNext](apId));
    }
    if (events.includes('modules')) {
      dispatch(interventionAsyncActions[getProgramModules](apId));
    }
    if (events.includes('library')) {
      dispatch(interventionAsyncActions[getProgramLibrary](apId));
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();

    sseAvailableRef.current = true;

    fetchEventSource(buildSseUrl(apId), {
      headers: buildHeaders(),
      signal: ctrl.signal,

      onopen: async (response) => {
        if (response.status === 401 || response.status === 404) {
          sseAvailableRef.current = false;
          throw new NonRetryableError(`SSE ${response.status} for ap ${apId}`);
        }
      },

      onmessage: (msg) => {
        const code = msg.event;

        if (!code) {
          return;
        }

        switch (code) {
          case 'step_completed':
            reload(['detail', 'modules', 'next']);
            break;
          case 'module_completed':
            reload(['modules', 'next']);
            break;
          case 'module_unlocked': {
            reload(['modules']);
            let moduleTitle = '';

            try {
              moduleTitle = JSON.parse(msg.data)?.moduleTitle ?? '';
            } catch {
              // event data may be absent
            }
            toast.success(moduleTitle ? `${moduleTitle} is now available` : 'A new module is now available');
            break;
          }
          case 'step_unlocked':
            reload(['modules', 'next']);
            break;
          case 'assignment_status_changed':
          case 'op_rule_action_executed':
            reload(['detail']);
            dispatch(interventionAsyncActions[getAssistantSession]());
            break;
          case 'content_assigned':
            reload(['library']);
            break;
          default:
            break;
        }
      },

      onerror: (err) => {
        if (err instanceof NonRetryableError) {
          throw err;
        }
        console.error(`SSE error for program ${apId}:`, err);
      }
    }).catch((err) => {
      if (!(err instanceof NonRetryableError)) {
        console.error(`SSE stream failed for program ${apId}:`, err);
      }
      sseAvailableRef.current = false;
    });

    return () => {
      ctrl.abort();
    };
  }, [apId, dispatch]);

  // Polling fallback at 30s — only fires when SSE is unavailable.
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!sseAvailableRef.current) {
        dispatch(interventionAsyncActions[getProgramDetail](apId));
        dispatch(interventionAsyncActions[getProgramNext](apId));
        dispatch(interventionAsyncActions[getProgramModules](apId));
        dispatch(interventionAsyncActions[getProgramLibrary](apId));
        dispatch(interventionAsyncActions[getProgramCareTeam](apId));
      }
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [apId, dispatch]);
};
